import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUrlState } from '@/lib/useUrlState';
import { aktePfad } from '@/lib/contact-path';
import {
  CAMPAIGN_MEMBER_STATES,
  CAMPAIGN_MEMBER_STATE_LABELS,
  FOLLOWUP_STATE_LABELS,
  type CampaignMemberState,
} from '@epasu/core/constants';
import type { AddCampaignMembersReport, CampaignRejectReason } from '@epasu/core';
import { crm, type CampaignMemberRow, type LeadListRow } from '@/lib/crm-client';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CAMPAIGN_REJECT_LABEL } from '@/lib/campaign-reject-reason';

interface Props {
  campaignId: string;
  /** Die Zielgruppe der Kampagne — nur ihre Listen kommen zum Nachziehen infrage. */
  audienceId: string;
  /** Meldet jede Änderung nach oben, damit Kopfzahlen mitziehen. */
  onChanged: () => void;
}

const STATE_VARIANTE: Record<CampaignMemberState, NonNullable<BadgeProps['variant']>> = {
  offen: 'neutral',
  wird_versendet: 'warning',
  versendet: 'success',
  uebersprungen: 'neutral',
  entfernt: 'neutral',
  fehlgeschlagen: 'danger',
};

/**
 * Der Mitglieder-Reiter der Kampagne.
 *
 * Zwei Zustände brauchen mehr als eine Badge: `fehlgeschlagen` bleibt sonst
 * für immer in jeder Welle hängen, `wird_versendet` ist der beanspruchte,
 * aber nie beantwortete Versuch — niemand weiß, ob die Mail raus ist. Beide
 * bekommen ausdrückliche Knöpfe statt einer automatischen Auflösung.
 */
export function CampaignMembersTab({ campaignId, audienceId, onChanged }: Props) {
  // In der URL, damit der Filter den Ausflug in eine Lead-Akte uebersteht.
  const [stateFilter, setStateFilter] = useUrlState<CampaignMemberState | ''>('zustand', '', [
    '',
    ...CAMPAIGN_MEMBER_STATES,
  ]);
  const [suche, setSuche] = useUrlState<string>('suche', '');
  const [rows, setRows] = useState<CampaignMemberRow[] | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aufnahmeBericht, setAufnahmeBericht] = useState<AddCampaignMembersReport | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [listen, setListen] = useState<LeadListRow[]>([]);
  const [ausListe, setAusListe] = useState('');
  const [listenBericht, setListenBericht] = useState<AddCampaignMembersReport | null>(null);
  const [beantwortet, setBeantwortet] = useState<CampaignMemberRow | null>(null);

  useEffect(() => {
    void crm.leadLists.list().then(setListen);
  }, []);
  const passendeListen = listen.filter((l) => !l.archived && l.audienceId === audienceId);

  const laden = useCallback(() => {
    setFehler(null);
    crm.campaigns
      .members(campaignId, stateFilter ? { state: stateFilter } : {})
      .then(setRows, (err: unknown) =>
        setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
      );
  }, [campaignId, stateFilter]);
  useEffect(laden, [laden]);

  const ladePending = useCallback(() => {
    void crm.campaigns.pendingFromSourceList(campaignId).then(setPending);
  }, [campaignId]);
  useEffect(ladePending, [ladePending]);

  const aktion = async (
    memberId: string,
    tun: () => Promise<{ updated: boolean } | void>,
  ) => {
    setBusyId(memberId);
    try {
      const ergebnis = await tun();
      // `updated: false` heißt: der Zustand war beim Schreiben nicht mehr
      // der, von dem diese Zeile ausging — ein anderer Vorgang (die Welle
      // selbst, oder ein zweiter Klick) ist dazwischengekommen. Kein Fehler,
      // aber der Klick verpuffte; die Liste zeigt gleich den echten Stand.
      if (ergebnis && !ergebnis.updated) {
        setFehler(
          'Zustand hat sich zwischenzeitlich geändert — die Aktion wurde nicht ausgeführt.',
        );
      }
      laden();
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const aufnehmen = async () => {
    setAufnahmeBericht(null);
    try {
      setAufnahmeBericht(await crm.campaigns.addFromSourceList(campaignId));
      laden();
      ladePending();
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Aufnehmen fehlgeschlagen');
    }
  };

  /**
   * Zieht aus einer beliebigen Liste nach, ohne die Quell-Liste umzuschreiben —
   * die bleibt die Angabe, woher der Stapel ursprünglich kam.
   */
  const ausListeAufnehmen = async () => {
    if (!ausListe) return;
    setListenBericht(null);
    setFehler(null);
    try {
      setListenBericht(await crm.campaigns.addFromList(campaignId, ausListe));
      laden();
      ladePending();
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Aufnehmen fehlgeschlagen');
    }
  };

  // In der Oberflaeche gefiltert: die Mitgliederliste kommt ohnehin ganz,
  // und so findet die Suche sofort beim Tippen.
  const begriff = suche.trim().toLowerCase();
  const sichtbar =
    rows && begriff
      ? rows.filter((m) =>
          [m.contactName, m.email, m.organizationName].some((feld) =>
            feld?.toLowerCase().includes(begriff),
          ),
        )
      : rows;

  return (
    <div className="space-y-4">
      {pending.length > 0 && (
        <Card className="space-y-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              <strong>{pending.length}</strong> neue geeignete{' '}
              {pending.length === 1 ? 'Lead' : 'Leads'} in der Quell-Liste.
            </p>
            <Button variant="outline" size="sm" onClick={() => void aufnehmen()}>
              Aufnehmen
            </Button>
          </div>
          {aufnahmeBericht && <Aufnahmebericht bericht={aufnahmeBericht} />}
        </Card>
      )}

      <Card className="space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm">Leads aus einer Liste aufnehmen:</p>
          <Select
            className="w-64"
            value={ausListe}
            onChange={(e) => {
              setAusListe(e.target.value);
              setListenBericht(null);
            }}
            aria-label="Liste"
          >
            <option value="">— Liste wählen —</option>
            {passendeListen.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.memberCount})
              </option>
            ))}
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={!ausListe}
            onClick={() => void ausListeAufnehmen()}
          >
            Aufnehmen
          </Button>
        </div>
        {listenBericht && <Aufnahmebericht bericht={listenBericht} />}
        {passendeListen.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Keine Lead-Liste in dieser Zielgruppe.
          </p>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Name, E-Mail oder Firma suchen …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            aria-label="Mitglieder durchsuchen"
          />
        </div>
        <Select
          className="w-56"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as CampaignMemberState | '')}
          aria-label="Zustand"
        >
          <option value="">Alle Zustände</option>
          {CAMPAIGN_MEMBER_STATES.map((s) => (
            <option key={s} value={s}>
              {CAMPAIGN_MEMBER_STATE_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>

      {fehler && (
        <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {fehler}
        </Card>
      )}

      <Card className="overflow-hidden">
        {rows === null ? (
          <p className="p-6 text-sm text-muted-foreground">Lade …</p>
        ) : !sichtbar || sichtbar.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            {begriff ? `Kein Mitglied passt zu „${suche.trim()}“.` : 'Keine Mitglieder in diesem Zustand.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Kontakt</th>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Zustand</th>
                  <th className="px-4 py-3 font-medium">Uhr</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((m) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <Link
                        to={aktePfad(m.contactId, 'lead')}
                        className="font-medium text-brand hover:underline"
                      >
                        {m.contactName}
                      </Link>
                      <p className="text-xs text-muted-foreground">{m.email ?? '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {m.organizationName ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATE_VARIANTE[m.state]}>
                        {CAMPAIGN_MEMBER_STATE_LABELS[m.state]}
                      </Badge>
                      {m.openState && (
                        <Badge
                          variant={m.openState === 'geoeffnet' ? 'success' : 'neutral'}
                          className="ml-1"
                        >
                          {m.openState === 'geoeffnet' ? 'geöffnet' : 'vielleicht geöffnet'}
                        </Badge>
                      )}
                      {m.state === 'fehlgeschlagen' && m.error && (
                        <p className="mt-1 text-xs text-destructive">{m.error}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {m.followupState ? FOLLOWUP_STATE_LABELS[m.followupState] : '—'}
                      {m.followupDueAt && (
                        <span className="ml-1">
                          ({new Date(m.followupDueAt).toLocaleDateString('de-DE')})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {m.state === 'fehlgeschlagen' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyId === m.id}
                              onClick={() =>
                                void aktion(m.id, () =>
                                  crm.campaigns.setMemberState(m.id, 'offen', {
                                    from: 'fehlgeschlagen',
                                  }),
                                )
                              }
                            >
                              Erneut versuchen
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === m.id}
                              onClick={() =>
                                void aktion(m.id, () =>
                                  crm.campaigns.setMemberState(m.id, 'uebersprungen', {
                                    from: 'fehlgeschlagen',
                                  }),
                                )
                              }
                            >
                              Überspringen
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === m.id}
                              onClick={() => void aktion(m.id, () => crm.campaigns.removeMember(m.id))}
                            >
                              Entfernen
                            </Button>
                          </>
                        )}
                        {m.state === 'wird_versendet' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyId === m.id}
                              onClick={() =>
                                void aktion(m.id, () =>
                                  crm.campaigns.setMemberState(m.id, 'versendet', {
                                    from: 'wird_versendet',
                                  }),
                                )
                              }
                            >
                              Als versendet markieren
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === m.id}
                              onClick={() =>
                                void aktion(m.id, () =>
                                  crm.campaigns.setMemberState(m.id, 'offen', {
                                    from: 'wird_versendet',
                                  }),
                                )
                              }
                            >
                              Erneut versuchen
                            </Button>
                          </>
                        )}

                        {/*
                          Der Vermerk von Hand, fuer Antworten, die die
                          Erkennung nicht findet — etwa eine neue Mail statt
                          einer Antwort im Verlauf, von einer fremden Adresse.
                          Auch nach beendeter Sequenz: sonst zaehlt die
                          Kampagne eine spaete Antwort nie.
                        */}
                        {m.followupState && m.followupState !== 'beantwortet' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyId === m.id}
                            onClick={() => setBeantwortet(m)}
                          >
                            Antwort erhalten
                          </Button>
                        )}

                        {/*
                          Der Gegenzug: eine Abwesenheitsnotiz ist keine
                          Antwort, und ein Klick daneben soll kein Endzustand
                          sein. Die Uhr laeuft danach ab der erreichten Stufe
                          weiter, nicht wieder von vorn.
                        */}
                        {m.followupState === 'beantwortet' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === m.id}
                            onClick={() =>
                              void aktion(m.id, async () => {
                                await crm.followups.reopen(m.contactId);
                              })
                            }
                          >
                            Antwort zurücknehmen
                          </Button>
                        )}

                        {/*
                          Uebersprungen sind vor allem die Firmengeschwister,
                          die eine Antwort mit angehalten hat. Nimmt jemand die
                          Antwort zurueck, muessen sie einzeln zurueck — welche
                          dazugehoerten, weiss danach niemand mehr.
                        */}
                        {m.state === 'uebersprungen' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === m.id}
                            onClick={() =>
                              void aktion(m.id, () =>
                                crm.campaigns.setMemberState(m.id, 'offen', {
                                  from: 'uebersprungen',
                                }),
                              )
                            }
                          >
                            Wieder aufnehmen
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {beantwortet && (
        <ConfirmDialog
          title="Antwort erhalten?"
          confirmLabel="Als beantwortet vermerken"
          onCancel={() => setBeantwortet(null)}
          onConfirm={async () => {
            await crm.followups.markAnswered(beantwortet.contactId);
            setBeantwortet(null);
            laden();
            onChanged();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Für {beantwortet.contactName} wird nicht mehr nachgefasst — auch nicht in
            anderen Kampagnen. Weitere Ansprechpartner derselben Firma werden in dieser
            Kampagne übersprungen, genau wie bei einer erkannten Antwort.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Liegt die Antwort als Mail im Posteingang, ordne sie stattdessen dort dem
            Kontakt zu: das hält die Uhr genauso an und hängt die Nachricht an den
            Kontakt.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Was eine Aufnahme ergeben hat.
 *
 * Beide Wege — Quell-Liste nachholen und aus einer beliebigen Liste
 * aufnehmen — melden denselben Bericht und zeigen ihn deshalb gleich.
 */
function Aufnahmebericht({ bericht }: { bericht: AddCampaignMembersReport }) {
  return (
    <p className="text-sm text-muted-foreground">
      {bericht.added} aufgenommen.
      {bericht.rejected.length > 0 && (
        <>
          {' '}
          Abgewiesen:{' '}
          {gruppiereAbgewiesene(bericht.rejected)
            .map(([grund, anzahl]) => `${anzahl} ${CAMPAIGN_REJECT_LABEL[grund]}`)
            .join(', ')}
          .
        </>
      )}
    </p>
  );
}

/** Wie viele Abgewiesene je Grund — für eine Zeile statt einer Liste je Kontakt. */
function gruppiereAbgewiesene(
  rejected: AddCampaignMembersReport['rejected'],
): [CampaignRejectReason, number][] {
  const zaehler = new Map<CampaignRejectReason, number>();
  for (const r of rejected) zaehler.set(r.reason, (zaehler.get(r.reason) ?? 0) + 1);
  return [...zaehler];
}
